import { 
  createScenario, 
  runSimulation, 
  getTaskStatus, 
  getSolution,
  uploadRides,
  calculateTimeWindowsWithUserAuth,
  generateFleet,
  createProblem
} from './api';
import { getToken } from '../../components/authentication/functions/authentication_management';
import { SimulationProgressTracker } from './progressTracker';
import { 
  runEnhancedSimulationWorkflow, 
  runChainedSimulationWorkflow,
  ChainedSimulationWorkflowInput,
  mapFleetToApiFleetSettings,
  buildSimpleTimeWindowSettings,
  pollTimeWindowCalculation
} from './orchestrator';
import { SimulationConfig, SimulationResults, SimulationProgress, TaskStatus, SimulationRunSummary } from '../../types/simulation';
import type { StepStatus } from '../../context/SimulationContext';
import { AppError, StructuredError } from '../../types/error.d'; // Add this line
import { 
  syntheticRideData, 
  syntheticFleetData, 
  syntheticTimeWindowData, 
  syntheticProblemConfig 
} from './syntheticData';
// Import debug functions for testing
import './apiDebug';
import { loggingService } from '../logging/loggingService';
import { DEFAULT_CLUSTERING_SETTINGS, DEFAULT_MATCHER_FLAGS, pashaRunSettingsFromRunBulk } from './clusteringDefaults';

import { getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';

export class SimulationService {
  private apiBaseUrl: string;

  constructor(apiBaseUrl?: string) {
    this.apiBaseUrl = apiBaseUrl || getSimulationApiBaseUrl();
  }

  private validateSimulationInputs(rides: any[], fleet: any): void {
    const hasStandardRides = rides.some(ride =>
      ride.passengers.some((p: any) => p.type === 'standard' && p.number_of_passengers > 0)
    );
    const hasWheelchairRides = rides.some(ride =>
      ride.passengers.some((p: any) => p.type === 'wheelchair' && p.number_of_passengers > 0)
    );

    let totalStandardCapacity = 0;
    let totalWheelchairCapacity = 0;

    fleet.vehicles.forEach((vehicleConfig: any) => {
      vehicleConfig.vehicle.capacity.forEach((capacity: any) => {
        if (capacity.type === 'standard') {
          totalStandardCapacity += capacity.number * vehicleConfig.number_of_vehicles;
        } else if (capacity.type === 'wheelchair') {
          totalWheelchairCapacity += capacity.number * vehicleConfig.number_of_vehicles;
        }
      });
    });

    if (hasStandardRides && totalStandardCapacity === 0) {
      throw new Error(
        'ValidationError: No trips were able to be delivered. ' +
        'All trips had standard passengers, but there were no vehicles in the fleet that have standard seats. ' +
        'Please add vehicles with standard seats to run this simulation successfully.'
      );
    }

    if (hasWheelchairRides && totalWheelchairCapacity === 0) {
      throw new Error(
        'ValidationError: No trips were able to be delivered. ' +
        'All trips had wheelchair passengers, but there were no vehicles in the fleet that have wheelchair accessible seats. ' +
        'Please add vehicles with wheelchair seats to run this simulation successfully.'
      );
    }
  }

  /**
   * Run enhanced simulation workflow with better progress tracking
   */
  async runEnhancedSimulation(
    config: SimulationConfig,
    onProgressUpdate: (progress: SimulationProgress) => void
  ): Promise<SimulationResults> {
    try {
      loggingService.debug('🚀 Starting enhanced simulation workflow...');

      this.validateSimulationInputs(config.rides, config.fleet);

      // Prepare the workflow input
      const workflowInput: ChainedSimulationWorkflowInput = {
        scenarioName: config.scenarioName,
        timeWindowSettings: config.timeWindowSettings,
        rides: config.rides,
        vehicles: config.fleet.vehicles,
        problemConfig: config.problem_config,
        fleet: config.fleet, // Pass full fleet object to preserve metadata (e.g., uatMetadata.serviceDate)
        run_bulk_simulation: config.run_bulk_simulation,
      };

      // Use the enhanced orchestrator with progress callbacks
      const result = await runEnhancedSimulationWorkflow(
        workflowInput,
        (step: string, progress: number, message: string) => {
          onProgressUpdate({
            taskId: 'enhanced-workflow',
            status: 'running',
            progress,
            message: `${step}: ${message}`
          });
        }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // Convert orchestrator result to SimulationResults format
      const taskResult = result.results?.task_result || result.results;
      const simulationResults: SimulationResults = {
        results_id: result.results?.task_result?.results_id || result.taskId || 'unknown',
        scenario_id: result.scenarioId || 'unknown',
        scenario_name: config.scenarioName,
        status: 'completed',
        summary: {
          assigned_rides: taskResult?.assigned_rides || 0,
          unassigned_rides: taskResult?.unassigned_rides || 0,
          total_rides: (taskResult?.assigned_rides || 0) + (taskResult?.unassigned_rides || 0) || config.rides.length,
          total_vehicles: result.results?.summary?.total_vehicles || config.fleet.vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0),
          total_distance: taskResult?.total_distance || 0,
          total_cost: taskResult?.total_cost || 0,
          average_wait_time: taskResult?.average_wait_time || 0
        },
        details: result.results
      };

      onProgressUpdate({
        taskId: result.taskId || 'workflow',
        status: 'completed',
        progress: 100,
        message: 'Simulation completed successfully!',
        results: simulationResults
      });

      return simulationResults;

    } catch (error: unknown) { // Use unknown for error type
      loggingService.error('❌ Enhanced simulation workflow failed', { category: 'SimulationService', error });
      const appError = this.handleSimulationError(error);
      onProgressUpdate({
        taskId: 'workflow',
        status: 'failed',
        progress: 0,
        message: appError.structuredError.what + (appError.structuredError.why ? `: ${appError.structuredError.why}` : '')
      });
      throw appError;
    }
  }

  /**
   * Create a comprehensive simulation configuration with enhanced options
   */
  createEnhancedSimulationConfig(
    scenarioName: string,
    rides: any[],
    fleet: any,
    options?: {
      timeWindowSettings?: any;
      problemConfig?: any;
      matcherFlags?: string[];
      optimizationLevel?: 'fast' | 'balanced' | 'thorough';
      travelTimeMultiplier?: number;
    }
  ): SimulationConfig {
    const generateUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };

    // Enhanced optimization settings based on level
    const optimizationSettings = {
      fast: {
        iteration_count: 50,
        travel_time_multiplier: 1.0,
        matcher_flags: ["skipRoundTripCheck", "matcher-faster-jsprit-convergence", "enable-legacy-path-descriptions"],
        max_time_minutes: 60,
        window: 600,
        slope: 1.5,
        intercept: 600
      },
      balanced: {
        iteration_count: 200,
        travel_time_multiplier: 1.1,
        matcher_flags: [...DEFAULT_MATCHER_FLAGS],
        max_time_minutes: 90,
        window: 1200,
        slope: 2.0,
        intercept: 1200
      },
      thorough: {
        iteration_count: 500,
        travel_time_multiplier: 1.2,
        matcher_flags: ["skipRoundTripCheck", "activity-time-setter-v2", "enable-legacy-path-descriptions"],
        max_time_minutes: 120,
        window: 1800,
        slope: 2.5,
        intercept: 1800
      }
    };

    const level = options?.optimizationLevel || 'balanced';
    const settings = optimizationSettings[level];

    // Enhanced time window settings
    const timeWindowSettings = {
      time_model_type: 'slope_and_intercept' as const,
      settings: {
        slope: settings.slope,
        intercept: settings.intercept,
        window: settings.window,
        max_time_minutes: settings.max_time_minutes,
        once_off_processing_time_seconds: 15,
        processing_time: [
          { seat_type: 'standard', processing_time_seconds: 30 },
          { seat_type: 'wheelchair', processing_time_seconds: 90 }
        ],
        ...options?.timeWindowSettings?.settings
      },
      ride_settings: {
        rides_name: 'simulation-rides',
        batch_id: null,
        ...options?.timeWindowSettings?.ride_settings
      }
    };

    // Enhanced problem configuration
    const problemConfig = {
      id: generateUUID(),
      configuration: {
        config_name: `${level}-optimization`,
        iteration_count: settings.iteration_count,
        travelTimeMultiplier: options?.travelTimeMultiplier || settings.travel_time_multiplier,
        detailed_path_required: true,
        ...options?.problemConfig?.configuration
      }
    };

    return {
      scenarioName,
      rides,
      timeWindowSettings,
      fleet,
      run_bulk_simulation: {
        matcher_flags: options?.matcherFlags || settings.matcher_flags,
        clustering: DEFAULT_CLUSTERING_SETTINGS,
      },
      problem_config: problemConfig
    };
  }

  /**
   * Run a complete simulation workflow with synthetic data for testing
   */
  async runSyntheticSimulation(
    onProgressUpdate: (progress: SimulationProgress) => void
  ): Promise<SimulationResults> {
    try {
      loggingService.debug('Starting synthetic simulation...');

      this.validateSimulationInputs(syntheticRideData.rides, syntheticFleetData.fleet);

      // Step 1: Upload Rides
      loggingService.debug('Step 1: Uploading rides...');
      const rideUploadResult = await uploadRides(
        syntheticRideData.rides,
        'synthetic-test-rides'
      );
      loggingService.debug('Rides uploaded', { category: 'SimulationService', batch_id: rideUploadResult.batch_id });

      // Step 2: Generate Fleet
      loggingService.debug('Step 2: Generating fleet...');
      const fleetResult = await generateFleet(
        syntheticFleetData.fleet,
        'synthetic-test-fleet'
      );
      loggingService.debug('Fleet generated', { category: 'SimulationService', fleet_id: fleetResult.fleet_id });

      // Step 3: Calculate Time Windows
      loggingService.debug('Step 3: Calculating time windows...');
      
      // Update the time window configuration to use the correct ride batch_id
      const timeWindowConfig = {
        ...syntheticTimeWindowData.timeWindow,
        ride_settings: {
          ...syntheticTimeWindowData.timeWindow.ride_settings,
          batch_id: rideUploadResult.batch_id // Use the actual ride batch ID
        }
      };
      
      const timeWindowResult = await calculateTimeWindowsWithUserAuth(
        timeWindowConfig,
        'synthetic-test-time-window'
      );
      loggingService.debug('Time windows calculation started', {
        category: 'SimulationService',
        time_window_batch_id: timeWindowResult.time_window_batch_id,
      });

      // Step 3.5: Wait for time windows calculation to complete
      loggingService.debug('Step 3.5: Waiting for time windows calculation to complete...');
      const { getTaskStatus } = await import('./api');
      
      let timeWindowTaskStatus;
      let attempts = 0;
      const maxAttempts = 30; // 5 minutes with 10-second intervals
      
      while (attempts < maxAttempts) {
        try {
          const rawResponse = await getTaskStatus(
            timeWindowResult.time_window_batch_id // This is actually the task_id
          );
          
          // Debug: Check what we actually received
          loggingService.debug('🔍 Raw response from getTaskStatus', { category: 'SimulationService', rawResponse });
          loggingService.debug('🔍 Raw response type', { category: 'SimulationService', type: typeof rawResponse });
          
          // If it's a string, try to parse it
          timeWindowTaskStatus = rawResponse;
          if (typeof rawResponse === 'string') {
            loggingService.debug('🔍 Response is a string, parsing JSON', { category: 'SimulationService' });
            timeWindowTaskStatus = JSON.parse(rawResponse);
          }
          
          // Debug: Log the entire response to see the structure
          loggingService.debug('🔍 Full timeWindowTaskStatus response', { category: 'SimulationService', timeWindowTaskStatus });
          loggingService.debug('🔍 Response type', { category: 'SimulationService', type: typeof timeWindowTaskStatus });
          loggingService.debug('🔍 Response keys', { category: 'SimulationService', keys: Object.keys(timeWindowTaskStatus || {}) });
          
          // Check for different possible status field names
          const currentStatus = timeWindowTaskStatus?.status || timeWindowTaskStatus?.task_status;
          const errorsRaised = timeWindowTaskStatus?.errors_raised;
          const hasErrorsRaised = errorsRaised && String(errorsRaised).trim().length > 0;
          
          loggingService.debug(`Time window calculation attempt ${attempts + 1}`, {
            category: 'SimulationService',
            status: timeWindowTaskStatus?.status,
            task_status: timeWindowTaskStatus?.task_status,
            currentStatus: currentStatus,
            completed_at: timeWindowTaskStatus?.completed_at,
            task_result: timeWindowTaskStatus?.task_result
          });
          
          if (currentStatus === 'COMPLETED' || currentStatus === 'completed') {
            loggingService.debug('✅ Time windows calculation completed successfully', { category: 'SimulationService' });
            
            // Debug: Log the entire task result structure
            loggingService.debug('🔍 Full task_result structure', {
              category: 'SimulationService',
              task_result: JSON.stringify(timeWindowTaskStatus?.task_result, null, 2),
            });
            loggingService.debug('🔍 task_result keys', {
              category: 'SimulationService',
              keys: Object.keys(timeWindowTaskStatus?.task_result || {}),
            });
            
            // Extract the actual time_window_batch_id from the task result
            if (timeWindowTaskStatus?.task_result && timeWindowTaskStatus.task_result.id) {
              const actualTimeWindowBatchId = timeWindowTaskStatus.task_result.id;
              loggingService.debug('📊 Extracted time_window_batch_id from task result', {
                category: 'SimulationService',
                actualTimeWindowBatchId,
              });
              
              // Update the time window result with the actual batch ID
              timeWindowResult.time_window_batch_id = actualTimeWindowBatchId;
            } else {
              loggingService.warn('⚠️ No time_window_batch_id found in task result, using task_id as fallback', {
                category: 'SimulationService',
              });
              loggingService.debug('⚠️ Available task_result fields', {
                category: 'SimulationService',
                keys: Object.keys(timeWindowTaskStatus?.task_result || {}),
              });
            }
            
            break;
          } else if (currentStatus === 'FAILED' || currentStatus === 'failed' || hasErrorsRaised) {
            const rawMsg = String(errorsRaised || timeWindowTaskStatus?.message || 'Unknown error');
            const isNoPathsFound = rawMsg.includes('No paths found');
            const howToFix = isNoPathsFound
              ? 'Please double-check that the basemap is available and try again.'
              : 'Review the simulation input and try again. If the issue persists, contact support.';
            throw new Error(`Time windows calculation failed: ${rawMsg}. ${howToFix}`);
          }
          
          // Wait 2 seconds before next poll
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        } catch (error) {
          // Rethrow task failure errors immediately - do not retry
          if (error instanceof Error && error.message.includes('Time window calculation failed')) {
            throw error;
          }
          loggingService.debug(`Time window calculation attempt ${attempts + 1} failed`, { category: 'SimulationService', error });
          await new Promise(resolve => setTimeout(resolve, 10000));
          attempts++;
        }
      }
      
      if (attempts >= maxAttempts) {
        throw new Error('Time windows calculation timed out after 5 minutes');
      }

      // Step 4: Create Problem Config
      loggingService.debug('Step 4: Creating problem config...');
      const problemConfigResult = await createProblem(
        syntheticProblemConfig
      );
      loggingService.debug('Problem config created', { category: 'SimulationService', problem_id: problemConfigResult.problem_id });

      // Step 5: Create Scenario with proper batch IDs
      loggingService.debug('Step 5: Creating scenario...');
      const scenarioPayload = {
        scenario_name: 'synthetic-test-scenario',
        ride_batch_id: rideUploadResult.batch_id,
        time_window_batch_id: timeWindowResult.time_window_batch_id,
        fleet_id: fleetResult.fleet_id,
        problem_config_id: problemConfigResult.problem_id,
        run_bulk_simulation: {
          matcher_flags: [...DEFAULT_MATCHER_FLAGS],
          clustering: DEFAULT_CLUSTERING_SETTINGS,
        },
      };
      
      loggingService.debug('🔍 Scenario payload with IDs', {
        category: 'SimulationService',
        ride_batch_id: rideUploadResult.batch_id,
        time_window_batch_id: timeWindowResult.time_window_batch_id,
        fleet_id: fleetResult.fleet_id,
        problem_config_id: problemConfigResult.problem_id
      });
      
      const scenarioResult = await createScenario(scenarioPayload);
      loggingService.debug('Scenario created', { category: 'SimulationService', scenario_id: scenarioResult.scenario_id });

      // Step 6: Run Simulation
      loggingService.debug('Step 6: Running simulation...');
      const simulationResult = await runSimulation(
        scenarioResult.scenario_id,
        'pasha_bulka',
        pashaRunSettingsFromRunBulk(scenarioPayload.run_bulk_simulation)
      );
      loggingService.debug('Simulation started', { category: 'SimulationService', task_id: simulationResult.task_id });

      // Step 7: Track Progress
      loggingService.debug('Step 7: Tracking progress...');
      const results = await SimulationProgressTracker.track(
        simulationResult.task_id,
        this.apiBaseUrl,
        onProgressUpdate
      );

      loggingService.debug('Synthetic simulation completed successfully!');
      return results;

    } catch (error: unknown) {
      loggingService.error('Synthetic simulation failed', { category: 'SimulationService', error });
      throw this.handleSimulationError(error);
    }
  }

  /**
   * Run a complete simulation workflow with real data from the UI
   * @param onPipelineProgress When set (e.g. wizard / map path), updates the six-step pipeline in {@link SimulationContext}
   * so {@link SimulationStepTrackerPanel} matches the Run Simulation modal behavior.
   */
  async runSimulation(
    config: SimulationConfig,
    onProgressUpdate: (progress: SimulationProgress) => void,
    onPipelineProgress?: (statuses: StepStatus[]) => void
  ): Promise<SimulationResults> {
    const pipe = onPipelineProgress;
    const allFailed: StepStatus[] = ['failed', 'failed', 'failed', 'failed', 'failed', 'failed'];
    try {
      loggingService.debug('Starting simulation...');

      this.validateSimulationInputs(config.rides, config.fleet);

      // Step 1: Upload Rides (mirrors SimulationModal setContextStepStatuses)
      pipe?.(['inProgress', 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted']);
      loggingService.debug('Step 1: Uploading rides...');
      const rideUploadResult = await uploadRides(
        config.rides,
        'simulation-rides'
      );
      loggingService.debug('Rides uploaded', { category: 'SimulationService', batch_id: rideUploadResult.batch_id });
      pipe?.(['success', 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted']);

      // Step 2: Generate Fleet
      pipe?.(['success', 'inProgress', 'notStarted', 'notStarted', 'notStarted', 'notStarted']);
      loggingService.debug('Step 2: Generating fleet...');
      const fleetResult = await generateFleet(
        config.fleet,
        'simulation-fleet'
      );
      loggingService.debug('Fleet generated', { category: 'SimulationService', fleet_id: fleetResult.fleet_id });

      // Step 3: Calculate Time Windows
      loggingService.debug('Step 3: Calculating time windows...');
      const timeWindowConfig = {
        ...config.timeWindowSettings,
        ride_settings: {
          ...config.timeWindowSettings?.ride_settings,
          batch_id: rideUploadResult.batch_id,
        },
      };

      pipe?.(['success', 'success', 'inProgress', 'notStarted', 'notStarted', 'notStarted']);
      const timeWindowResult = await calculateTimeWindowsWithUserAuth(
        timeWindowConfig,
        'simulation-time-window'
      );
      loggingService.debug('Time windows calculated', {
        category: 'SimulationService',
        time_window_batch_id: timeWindowResult.time_window_batch_id,
      });

      const timeWindowPoll = await pollTimeWindowCalculation(timeWindowResult.time_window_batch_id);
      pipe?.(['success', 'success', 'success', 'notStarted', 'notStarted', 'notStarted']);

      // Step 4: Create Problem Config
      loggingService.debug('Step 4: Creating problem config...');
      const problemConfigResult = await createProblem(
        config.problem_config
      );
      loggingService.debug('Problem config created', { category: 'SimulationService', problem_id: problemConfigResult.problem_id });
      pipe?.(['success', 'success', 'success', 'inProgress', 'notStarted', 'notStarted']);

      // Step 5: Create Scenario with proper batch IDs
      loggingService.debug('Step 5: Creating scenario...');
      const scenarioPayload = {
        scenario_name: config.scenarioName,
        ride_batch_id: rideUploadResult.batch_id,
        time_window_batch_id: timeWindowPoll.time_window_batch_id,
        fleet_id: fleetResult.fleet_id,
        problem_config_id: problemConfigResult.problem_id,
        ...(config.run_bulk_simulation ? { run_bulk_simulation: config.run_bulk_simulation } : {}),
      };
      
      loggingService.debug('🔍 Scenario payload with IDs', {
        category: 'SimulationService',
        ride_batch_id: rideUploadResult.batch_id,
        time_window_batch_id: timeWindowPoll.time_window_batch_id,
        fleet_id: fleetResult.fleet_id,
        problem_config_id: problemConfigResult.problem_id
      });
      
      const scenarioResult = await createScenario(scenarioPayload);
      loggingService.debug('Scenario created', { category: 'SimulationService', scenario_id: scenarioResult.scenario_id });
      pipe?.(['success', 'success', 'success', 'success', 'inProgress', 'notStarted']);

      // Step 6: Run Simulation
      loggingService.debug('Step 6: Running simulation...');
      pipe?.(['success', 'success', 'success', 'success', 'success', 'inProgress']);
      const simulationResult = await runSimulation(
        scenarioResult.scenario_id,
        'pasha_bulka',
        pashaRunSettingsFromRunBulk(config.run_bulk_simulation)
      );
      loggingService.debug('Simulation started', { category: 'SimulationService', task_id: simulationResult.task_id });

      // Step 7: Track Progress
      loggingService.debug('Step 7: Tracking progress...');
      const results = await SimulationProgressTracker.track(
        simulationResult.task_id,
        this.apiBaseUrl,
        (progress) => {
          const status = String(progress.status ?? '').toLowerCase();
          if (pipe) {
            if (status === 'completed') {
              pipe(['success', 'success', 'success', 'success', 'success', 'success']);
            } else if (status === 'failed') {
              pipe(['success', 'success', 'success', 'success', 'success', 'failed']);
            } else {
              pipe(['success', 'success', 'success', 'success', 'success', 'inProgress']);
            }
          }
          onProgressUpdate(progress);
        }
      );

      loggingService.debug('Simulation completed successfully!');
      return results;

    } catch (error: unknown) {
      pipe?.(allFailed);
      loggingService.error('Simulation failed', { category: 'SimulationService', error });
      throw this.handleSimulationError(error);
    }
  }

  /**
   * Get detailed simulation results
   */
  async getSimulationResults(resultsId: string): Promise<any> {
    try {
      loggingService.debug('Fetching detailed simulation results...');
      const results = await getSolution(resultsId);
      loggingService.debug('Results fetched successfully');
      return results;
    } catch (error) {
      loggingService.error('Failed to fetch simulation results', { category: 'SimulationService', error });
      throw this.handleSimulationError(error);
    }
  }

  /**
   * Get task status
   */
  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    try {
      const status = await getTaskStatus(taskId);
      return status;
    } catch (error) {
      loggingService.error('Failed to get task status', { category: 'SimulationService', error });
      throw this.handleSimulationError(error);
    }
  }

  /**
   * Normalize any error to AppError. Use this when catching errors to ensure structured display.
   */
  static normalizeError(error: unknown): AppError {
    let structuredError: StructuredError;

    if (error instanceof AppError) {
      return error; // Already an AppError, return as is
    }

    if (error instanceof Error) {
      if (error.message?.startsWith('ValidationError:')) {
        const messageParts = error.message.split('. ');
        structuredError = {
          what: messageParts[0].replace('ValidationError:', '').trim(),
          why: messageParts[1] || '',
          howToFix: messageParts[2] || '',
          category: 'Validation',
          details: error,
        };
      } else if (error.message?.includes('API error')) {
        structuredError = {
          what: 'Simulation API Call Failed',
          why: error.message,
          howToFix: 'Please check your network connection and API service status. If the issue persists, contact support.',
          category: 'API',
          details: error,
        };
      } else if (error.message?.includes('authentication')) {
        structuredError = {
          what: 'Authentication Required',
          why: 'Your session may have expired or you are not logged in.',
          howToFix: 'Please log in again to continue.',
          category: 'Authentication',
          details: error,
        };
      } else if (error.message?.includes('timeout')) {
        structuredError = {
          what: 'Simulation Timed Out',
          why: 'The simulation took too long to complete.',
          howToFix: 'Try again with a smaller dataset or simpler configuration.',
          category: 'Network',
          details: error,
        };
      } else if (error.message?.includes('No paths found')) {
        structuredError = {
          what: 'Basemap Unavailable',
          why: 'Rides and fleet uploaded successfully (auth is fine), and our checks show rides and fleet are on the same day. The failure happened when calculating travel times between locations—the basemap routing service returned "No paths found". This usually means the basemap (OSRM/Mapbox) is unavailable or the routing server cannot be reached.',
          howToFix: 'Please double-check that the basemap is available and try again.',
          category: 'API',
          details: error,
        };
      } else {
        structuredError = {
          what: 'Simulation Failed',
          why: error.message || 'An unknown error occurred during simulation.',
          howToFix: 'Please review the input data and try again. If the issue persists, contact support.',
          category: 'Unknown',
          details: error,
        };
      }
    } else {
      // Handle non-Error objects
      structuredError = {
        what: 'An Unexpected Error Occurred',
        why: 'The system encountered an unhandled error.',
        howToFix: 'Please try again. If the issue persists, contact support with details of what you were doing.',
        category: 'Unknown',
        details: error,
      };
    }

    return new AppError(structuredError);
  }

  /**
   * Handle simulation errors and map them to our error types
   */
  private handleSimulationError(error: unknown): AppError {
    return SimulationService.normalizeError(error);
  }

  /**
   * Test individual endpoints with synthetic data
   */
  async testEndpoint(endpoint: string, syntheticData: any): Promise<any> {
    loggingService.debug(`Testing endpoint: ${endpoint}`);
    
    try {
      switch (endpoint) {
        case 'uploadRides':
          return await uploadRides(syntheticData.rides, syntheticData.name);
        
        case 'calculateTimeWindows':
          return await calculateTimeWindowsWithUserAuth(
            syntheticData.timeWindow, 
            syntheticData.name
          );
        
        case 'generateFleet':
          return await generateFleet(syntheticData.fleet, syntheticData.name);
        
        case 'createProblem':
          return await createProblem(syntheticData);
        
        case 'createScenario':
          return await createScenario(syntheticData);
        
        default:
          throw new Error(`Unknown endpoint: ${endpoint}`);
      }
    } catch (error) {
      loggingService.error(`Endpoint test failed for ${endpoint}`, { category: 'SimulationService', error });
      throw error;
    }
  }

  /**
   * Fetch my simulation results from /results/my_results endpoint
   */
  async fetchMyResults(authorization?: string): Promise<SimulationRunSummary[]> {
    try {
      const token = authorization || getToken()?.access_token;
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${this.apiBaseUrl}/results/my_results`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch my results (${response.status}): ${errorText || response.statusText}`);
      }

      const data = await response.json();

      const pickScenarioDisplayName = (item: Record<string, unknown>): string => {
        for (const key of ['scenario_name', 'scenarioName', 'name', 'scenario_title'] as const) {
          const v = item[key];
          if (typeof v === 'string' && v.trim() !== '') return v.trim();
        }
        return '';
      };

      const pickTripCount = (item: Record<string, unknown>): number | undefined => {
        const numberFrom = (value: unknown): number | undefined =>
          typeof value === 'number' && Number.isFinite(value) ? value : undefined;

        const directCandidates: unknown[] = [
          item.no_rides,
          item.num_requests,
          item.numRequests,
          item.total_rides,
          item.totalRequests,
          item.total_requests,
          item.rides_count,
          item.number_of_rides,
          item.request_count,
          item.no_requests,
        ];
        for (const candidate of directCandidates) {
          const value = numberFrom(candidate);
          if (value != null) return value;
        }

        const summary = item.summary;
        if (summary && typeof summary === 'object') {
          const s = summary as Record<string, unknown>;
          const summaryCandidates: unknown[] = [
            s.total_rides,
            s.num_requests,
            s.numRequests,
            s.totalRequests,
            s.total_requests,
            s.no_rides,
            s.rides_count,
            s.number_of_rides,
            s.request_count,
          ];
          for (const candidate of summaryCandidates) {
            const value = numberFrom(candidate);
            if (value != null) return value;
          }

          const assigned = numberFrom(s.assigned_rides) ?? 0;
          const unassigned = numberFrom(s.unassigned_rides) ?? 0;
          if (assigned > 0 || unassigned > 0) return assigned + unassigned;
        }

        const assigned = numberFrom(item.assigned_rides) ?? 0;
        const unassigned = numberFrom(item.unassigned_rides) ?? 0;
        if (assigned > 0 || unassigned > 0) return assigned + unassigned;

        return undefined;
      };

      const normalizeSummary = (item: any): SimulationRunSummary => {
        const resultsIdCandidate =
          item?.results_id ??
          item?.result_id ??
          item?.simulation_result_id ??
          item?.task_result_id ??
          item?.id ??
          item?.task_result?.results_id ??
          // Final fallback: many APIs use run_id and results_id interchangeably.
          // Using run_id prevents unnecessary list-refresh round-trips when the
          // caller needs a results_id for subsequent detail fetches.
          item?.run_id;

        if (
          !(typeof item?.results_id === 'string' && item.results_id.trim() !== '') &&
          typeof resultsIdCandidate === 'string' &&
          resultsIdCandidate.trim() !== ''
        ) {
          loggingService.warn('results_id missing from API response, falling back to alternate field', {
            category: 'SimulationService',
            fallbackField: item?.run_id === resultsIdCandidate ? 'run_id' : 'id/task_result_id',
            runId: item?.run_id,
          });
        }

        const scenario_name = pickScenarioDisplayName(item as Record<string, unknown>);
        const no_rides = pickTripCount(item as Record<string, unknown>);

        return {
          ...item,
          results_id:
            typeof resultsIdCandidate === 'string' && resultsIdCandidate.trim() !== ''
              ? resultsIdCandidate
              : item?.results_id,
          scenario_name,
          no_rides,
        } as SimulationRunSummary;
      };

      // Handle both array and object responses
      if (Array.isArray(data)) {
        return data.map(normalizeSummary);
      }
      if (Array.isArray(data.data)) {
        return data.data.map(normalizeSummary);
      }
      if (data.results && Array.isArray(data.results)) {
        return data.results.map(normalizeSummary);
      }
      return [];
    } catch (error) {
      loggingService.error('Failed to fetch my results', { category: 'SimulationService', error });
      throw error;
    }
  }
}

// Export a default instance
export const simulationService = new SimulationService(); 
